import Vuex from 'vuex';
import apiClient from '../api';

let sessionRestorePromise = null;

const store = new Vuex.Store({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    role: localStorage.getItem('role') || null,
    id: localStorage.getItem('id') || null,
    role_id: localStorage.getItem('role_id') || null,
    userPermissions: JSON.parse(localStorage.getItem('userPermissions')) || {},
    sessionReady: false,
    hasShownShipmentReminder: false, // Add new state for the reminder

    // New state for AlertDialog
    alertDialogVisible: false,
    alertDialogTitle: '',
    alertDialogMessage: '', // Reintroduced message state
    alertDialogType: 'info', // Default type
    alertDialogMaxWidth: 400, // New state for maxWidth
    todayDeliveryOrdersData: [], // New state to store raw delivery orders data
  },

  mutations: {
    setUser(state, user) {
      state.user = user || null;
      state.role = user?.role || null;
      state.id = user?.id || null;
      state.role_id = user?.role_id || null;

      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('role', user.role);
        localStorage.setItem('id', user.id);
        localStorage.setItem('role_id', user.role_id);
      }
    },

    clearUser(state) {
      state.user = null;
      state.role = null;
      state.id = null;
      state.role_id = null;
      state.userPermissions = {};
      state.hasShownShipmentReminder = false; // Reset on logout
      sessionStorage.removeItem('deliveryReminderShown'); // Clear session storage flag on logout

      // Reset AlertDialog state on logout
      state.alertDialogVisible = false;
      state.alertDialogTitle = '';
      state.alertDialogMessage = ''; // Reset message
      state.alertDialogType = 'info';
      state.alertDialogMaxWidth = 400;
      state.todayDeliveryOrdersData = []; // Clear data on logout

      localStorage.removeItem('user');
      localStorage.removeItem('role');
      localStorage.removeItem('id');
      localStorage.removeItem('role_id');
      localStorage.removeItem('userPermissions');
    },

    setPermissions(state, permissions) {
      state.userPermissions = permissions || {};
      localStorage.setItem('userPermissions', JSON.stringify(permissions));
    },

    setSessionReady(state, ready) {
      state.sessionReady = ready;
    },

    setHasShownShipmentReminder(state, value) {
      state.hasShownShipmentReminder = value;
    },

    // Updated mutation to show the AlertDialog, now accepts message, data, and maxWidth
    showAlertDialog(state, { title, type = 'info', message = '', data = [], maxWidth = 400 }) {
      state.alertDialogVisible = true;
      state.alertDialogTitle = title;
      state.alertDialogType = type;
      state.alertDialogMessage = message; // Set message for simple alerts
      state.todayDeliveryOrdersData = data; // Store the raw data for table alerts
      state.alertDialogMaxWidth = maxWidth; // Set max-width
    },
    // Mutation to hide the AlertDialog
    hideAlertDialog(state) {
      state.alertDialogVisible = false;
      state.alertDialogTitle = '';
      state.alertDialogMessage = ''; // Clear message
      state.alertDialogType = 'info';
      state.alertDialogMaxWidth = 400;
      state.todayDeliveryOrdersData = []; // Clear data when hidden
    }
  },

  actions: {
    async login({ commit, dispatch }, credentials) {
      // console.log("💡 [Login Action] Login action triggered."); // Removed for cleanup
      try {
        const res = await apiClient.post('/login', credentials);
        const user = res.data.user;
        commit('setUser', user);
        commit('setPermissions', user.permissions || {});

        // --- Trigger shipment reminder check ---
        await dispatch('checkShipmentReminder');

        return user;
      } catch (error) {
        console.error("❌ [Login Action] Login failed:", error.response?.data || error.message);
        throw error; // Re-throw to propagate the error to the component
      }
    },

    async checkShipmentReminder({ commit, state }, { force = false } = {}) {
      const reminderShownInSession = sessionStorage.getItem('deliveryReminderShown');
      const isEligibleRole = (state.role === 'admin' || state.role === 'sales');
      
      // If force is true, we ignore the sessionStorage flag
      const shouldAttemptReminder = isEligibleRole && (force || !reminderShownInSession);

      // console.log("💡 [Reminder Debug] sessionStorage 'deliveryReminderShown':", reminderShownInSession); // Removed for cleanup
      // console.log("💡 [Reminder Debug] User role:", state.role); // Removed for cleanup
      // console.log("💡 [Reminder Debug] Is eligible role (admin/sales):", isEligibleRole); // Removed for cleanup
      // console.log("💡 [Reminder Debug] Should attempt reminder check (combined condition):", shouldAttemptReminder); // Removed for cleanup

      if (shouldAttemptReminder) {
        // console.log("💡 [Reminder Debug] Attempting to fetch today's delivery orders..."); // Removed for cleanup
        try {
          const response = await apiClient.get('/deliveryOrders/today');
          const todayDeliveryOrders = response.data;

          // console.log("💡 [Reminder Debug] API response for today's delivery orders:", todayDeliveryOrders); // Removed for cleanup

          if (todayDeliveryOrders && todayDeliveryOrders.length > 0) {
            commit('showAlertDialog', {
              title: '今日出貨提醒',
              type: 'warning',
              data: todayDeliveryOrders,
              maxWidth: 600
            });
            // console.log("💡 [Reminder Debug] AlertDialog committed with data."); // Removed for cleanup
            if (!force) {
              sessionStorage.setItem('deliveryReminderShown', 'true');
              // console.log("💡 [Reminder Debug] 'deliveryReminderShown' set to 'true' in sessionStorage."); // Removed for cleanup
            }
          } else {
            // console.log("💡 [Reminder Debug] No delivery orders found for today."); // Removed for cleanup
            commit('showAlertDialog', {
              title: '今日出貨提醒',
              type: 'info',
              message: '今日沒有貨要出'
            });
          }
        } catch (deliveryError) {
          console.error("❌ [Reminder] Error fetching today's delivery orders:", deliveryError);
          commit('showAlertDialog', {
            title: '錯誤',
            message: '無法獲取今日出貨訂單資訊。',
            type: 'error'
          });
        }
      }
    },

    async logout({ commit }) {
      try {
        await apiClient.post('/logout');
      } catch (error) {
        // Logging out is a local action first and foremost: clearUser()
        // below always runs regardless, so a failed server-side logout
        // call must not become an unhandled rejection for callers like
        // DashBoradSidebar.vue that dispatch('logout') without a .catch().
        console.error('❌ [Logout Action] Server-side logout failed:', error.response?.data || error.message);
      } finally {
        commit('clearUser');
      }
    },

    async restoreSession({ commit }, { skipAuthRedirect = false } = {}) {
      if (!sessionRestorePromise) {
        sessionRestorePromise = (async () => {
          try {
            const response = await apiClient.get('/profile', { skipAuthRedirect });
            commit('setUser', response.data);
            commit('setPermissions', response.data.permissions || {});
            return response.data;
          } catch (error) {
            commit('clearUser');
            return null;
          } finally {
            commit('setSessionReady', true);
            sessionRestorePromise = null;
          }
        })();
      }
      return sessionRestorePromise;
    },

    async fetchPermissions({ commit, state }) {
      const roleId = state.role_id;
      if (!roleId) return;

      try {
        const res = await apiClient.get(`/roles/${roleId}/permissions`);
        commit('setPermissions', res.data);
      } catch (error) {
        console.error('❌ Fetch permissions failed:', error);
        commit('setPermissions', {});
      }
    }
  },

  getters: {
    isAuthenticated: (state) => !!(state.user && state.role && state.id),
    isSessionReady: (state) => state.sessionReady,

    getUserRole: (state) => state.role,
    getUserId: (state) => state.id,
    getUsername: (state) => state.user?.name || state.user?.username || '未登入',
    isAdmin: (state) => state.role === 'admin',

    canView: (state) => (moduleKey) => {
      if (moduleKey === 'role_permissions') return state.role === 'admin';
      if (state.role === 'admin') return true;
      return state.userPermissions[moduleKey]?.view === true;
    },

    can: (state) => (moduleKey, action) => {
      if (moduleKey === 'role_permissions') return state.role === 'admin';
      if (state.role === 'admin') return true;
      return state.userPermissions[moduleKey]?.[action] === true;
    },

    canCreate: (state, getters) => (moduleKey) => getters.can(moduleKey, 'create'),
    canEdit: (state, getters) => (moduleKey) => getters.can(moduleKey, 'edit'),

    // Getters for AlertDialog
    getAlertDialogVisible: (state) => state.alertDialogVisible,
    getAlertDialogTitle: (state) => state.alertDialogTitle,
    getAlertDialogMessage: (state) => state.alertDialogMessage, // Reintroduced getter
    getAlertDialogType: (state) => state.alertDialogType,
    getAlertDialogMaxWidth: (state) => state.alertDialogMaxWidth, // New getter
    getTodayDeliveryOrdersData: (state) => state.todayDeliveryOrdersData, // New getter for the data
  }
});

export default store;
